import React from 'react';
import { string, func, node } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './ProductionStageArc.module.css';

// The standardized 5-stage production arc. `key` is what's stored on the
// listing (publicData.status); labels come from the translation catalog.
export const PRODUCTION_STAGES = [
  { key: 'concept', labelId: 'ProductionStageArc.concept' },
  { key: 'development', labelId: 'ProductionStageArc.development' },
  { key: 'rehearsal', labelId: 'ProductionStageArc.rehearsal' },
  { key: 'run', labelId: 'ProductionStageArc.run' },
  { key: 'archive', labelId: 'ProductionStageArc.archive' },
];

/**
 * A horizontal 5-step arc: Concept → Devt. → Rehearsal → Run → Archive.
 * Editable when `onChange` is provided (steps are buttons); otherwise it
 * renders read-only for the public Show Page. The current step and every step
 * before it read as "reached"; later steps are muted.
 */
const ProductionStageArc = ({ value, onChange, className, label }) => {
  const activeIndex = PRODUCTION_STAGES.findIndex(s => s.key === value);
  const editable = typeof onChange === 'function';

  return (
    <div className={classNames(css.root, className)}>
      {label ? <span className={css.label}>{label}</span> : null}
      <ol className={css.arc}>
        {PRODUCTION_STAGES.map((stage, index) => {
          const reached = activeIndex >= 0 && index <= activeIndex;
          const isCurrent = index === activeIndex;
          const stepClasses = classNames(css.step, {
            [css.stepReached]: reached,
            [css.stepCurrent]: isCurrent,
          });
          const content = (
            <>
              <span className={css.dot} aria-hidden="true" />
              <span className={css.stepLabel}>
                <FormattedMessage id={stage.labelId} />
              </span>
            </>
          );
          return (
            <li key={stage.key} className={css.stepItem}>
              {index > 0 && (
                <span
                  className={classNames(css.connector, { [css.connectorReached]: reached })}
                  aria-hidden="true"
                />
              )}
              {editable ? (
                <button
                  type="button"
                  className={stepClasses}
                  aria-pressed={isCurrent}
                  onClick={() => onChange(stage.key)}
                >
                  {content}
                </button>
              ) : (
                <span className={stepClasses}>{content}</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

ProductionStageArc.defaultProps = {
  value: null,
  onChange: null,
  className: null,
  label: null,
};

ProductionStageArc.propTypes = {
  value: string,
  onChange: func,
  className: string,
  label: node,
};

export default ProductionStageArc;
